import { useState } from "react";
import { getSupabaseClient } from "../../lib/supabase";

const brandPrimary = "#2C2731";
const brandSurface = "#FFFCEF";
const brandSurfaceAlt = "#F2EAC3";

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(44,39,49,0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  zIndex: 50,
};

const modalStyle: React.CSSProperties = {
  width: "min(500px, 100%)",
  background: brandSurface,
  borderRadius: 24,
  border: `1px solid ${brandPrimary}`,
  padding: 24,
  boxShadow: "0 24px 48px rgba(44,39,49,0.35)",
  color: brandPrimary,
};

const titleStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 600,
  color: brandPrimary,
  marginBottom: 16,
};

const textStyle: React.CSSProperties = {
  fontSize: 14,
  color: brandPrimary,
  lineHeight: 1.6,
  marginBottom: 24,
};

const warningBoxStyle: React.CSSProperties = {
  border: "1px solid #DC2626",
  borderRadius: 8,
  padding: 16,
  background: "#FEE2E2",
  marginBottom: 24,
};

const warningTextStyle: React.CSSProperties = {
  fontSize: 13,
  color: "#991B1B",
  fontWeight: 500,
};

const buttonRowStyle: React.CSSProperties = {
  display: "flex",
  gap: 12,
  justifyContent: "flex-end",
};

const buttonBaseStyle: React.CSSProperties = {
  padding: "10px 20px",
  borderRadius: 8,
  border: `1px solid ${brandPrimary}`,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.2s ease",
};

const cancelButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  background: brandSurface,
  color: brandPrimary,
};

const confirmButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  background: "#DC2626",
  color: "#FFFFFF",
  borderColor: "#DC2626",
};

const disabledButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  background: "#D1D5DB",
  color: "#6B7280",
  borderColor: "#D1D5DB",
  cursor: "not-allowed",
  opacity: 0.6,
};

const successBoxStyle: React.CSSProperties = {
  border: "1px solid #059669",
  borderRadius: 8,
  padding: 16,
  background: "#D1FAE5",
  marginBottom: 16,
};

const successTextStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#065F46",
  fontWeight: 500,
};

const errorBoxStyle: React.CSSProperties = {
  border: "1px solid #DC2626",
  borderRadius: 8,
  padding: 16,
  background: "#FEE2E2",
  marginBottom: 16,
};

const errorTextStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#991B1B",
  fontWeight: 500,
};

interface ConfirmDestroyRoomsModalProps {
  onClose: () => void;
  onConfirm: () => void;
}

export function ConfirmDestroyRoomsModal({ onClose, onConfirm }: ConfirmDestroyRoomsModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ message: string; rowsAffected?: number } | null>(null);

  const handleConfirm = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const supabase = getSupabaseClient();

      // Call the destroy-rooms edge function using Supabase invoke
      const { data, error } = await supabase.functions.invoke("destroy-rooms", {
        method: "POST",
      });

      if (error) {
        throw new Error(error.message || "Failed to reset message rooms");
      }

      setSuccess({
        message: data?.message || "All message rooms have been reset successfully",
        rowsAffected: data?.rowsAffected,
      });

      // Call the parent's onConfirm after a short delay to show success
      setTimeout(() => {
        onConfirm();
      }, 2000);
    } catch (err) {
      console.error("Error destroying rooms:", err);
      setError(err instanceof Error ? err.message : "Failed to reset message rooms");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  return (
    <div style={backdropStyle} onClick={handleBackdropClick}>
      <div style={modalStyle}>
        <h2 style={titleStyle}>Confirm Reset All Message Rooms</h2>

        {!success && (
          <>
            <div style={textStyle}>
              Are you sure you want to reset all message rooms? This will:
            </div>

            <div style={warningBoxStyle}>
              <div style={warningTextStyle}>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Hide all existing messages from users</li>
                  <li>Create fresh chat rooms for all conversations</li>
                  <li>Affect all guides and seekers immediately</li>
                </ul>
              </div>
            </div>

            <div style={warningBoxStyle}>
              <div style={warningTextStyle}>
                Due to rooms having different versions, this action is <strong>IRREVERSIBLE</strong>.
              </div>
            </div>

            <div style={{ ...textStyle, marginBottom: 16 }}>
              Messages will remain with the provider until expiry but will not be visible to users.
            </div>
          </>
        )}

        {error && (
          <div style={errorBoxStyle}>
            <div style={errorTextStyle}>
              {error}
            </div>
          </div>
        )}

        {success && (
          <div style={successBoxStyle}>
            <div style={successTextStyle}>
              {success.message}
              {success.rowsAffected !== undefined && (
                <div style={{ marginTop: 8 }}>
                  {success.rowsAffected} conversation{success.rowsAffected !== 1 ? "s" : ""} affected
                </div>
              )}
            </div>
          </div>
        )}

        <div style={buttonRowStyle}>
          {!success && (
            <>
              <button
                style={cancelButtonStyle}
                onClick={onClose}
                disabled={isLoading}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.background = brandSurfaceAlt;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = brandSurface;
                }}
              >
                Cancel
              </button>
              <button
                style={isLoading ? disabledButtonStyle : confirmButtonStyle}
                onClick={handleConfirm}
                disabled={isLoading}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.background = "#B91C1C";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.background = "#DC2626";
                  }
                }}
              >
                {isLoading ? "Resetting..." : "Yes, Reset All Rooms"}
              </button>
            </>
          )}
          {success && (
            <button
              style={buttonBaseStyle}
              onClick={onClose}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = brandSurfaceAlt;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = brandSurface;
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
